import React from "react";
import { Row, Col } from "react-bootstrap";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const amazonUrl = process.env.REACT_APP_AMAZON_AEREO_URL || ''

const AereoSummary = ({ envisionData }) => {
  if (!envisionData) return null;

  const segments = envisionData.choosenFlight?.segments || {};
  const airportsMap = segments.airports || {};
  const passengers = envisionData.choosenFlight?.passengers || [];

  const passengersCount = (() => {
    if (Array.isArray(passengers) && passengers.length > 0) {
      return passengers.length;
    }

    if (Array.isArray(segments.fares)) {
      return segments.fares.reduce(
        (sum, fare) => sum + (Number(fare.Quantity) || 0),
        0,
      );
    }

    return 0;
  })();

  const cabinLabelByKey = {
    Economy: "Econômica",
    "Standard economy": "Econômica",
    PremiumEconomy: "Econômica",
    Business: "Executiva",
    Executive: "Executiva",
    First: "Executiva",
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";
    return format(date, "dd MMM yyyy", { locale: ptBR });
  };

  const getStopsLabel = (stops) => {
    const n = Number(stops);
    if (!Number.isFinite(n) || n <= 0) return "Direto";
    return `${n} conex${n > 1 ? "ões" : "ão"}`;
  };

  const makeAirport = (code) => {
    const a = airportsMap?.[code] || {};
    return { code: a.Code || code, cityName: a.CityName || "" };
  };

  const toSegment = (container) => {
    const flights = container?.Flights || [];
    if (!Array.isArray(flights) || flights.length === 0) return null;

    const departureAirport = makeAirport(container.DepartureStation);
    const destinationAirport = makeAirport(container.ArrivalStation);

    return {
      departureCode: container.DepartureStation,
      arrivalCode: container.ArrivalStation,
      departureDate: container.DepartureDateTime,
      arrivalDate: container.ArrivalDateTime,
      company:
        segments.validatingCarrier ||
        container.ValidatingCarrier ||
        flights[0]?.CarrierCode ||
        "",
      stops: flights.length - 1,
      flights: flights.map((f) => ({
        departureDate: f.DepartureDateTime,
        arrivalDate: f.ArrivalDateTime,
        carrierCode: f.CarrierCode || f.OperatedBy || "",
        cabin: f.FareOption?.Cabin || "",
        departureStation: f.DepartureStation,
        arrivalStation: f.ArrivalStation,
        departureAirport: makeAirport(f.DepartureStation),
        arrivalAirport: makeAirport(f.ArrivalStation),
        flightNumber: f.FlightNumber || "",
      })),
      departureAirport,
      destinationAirport,
    };
  };

  const departureLegs = (() => {
    const dep = toSegment(segments.departureFlights);
    return dep ? [dep] : [];
  })();

  const returnLegs = (() => {
    const ret = toSegment(segments.returnFlights);
    return ret ? [ret] : [];
  })();

  const getCabinLabel = (cabin) => cabinLabelByKey[cabin] || cabin || "Econômica";

  const CabinBadge = ({ cabin }) => { // eslint-disable-line react/prop-types
    const label = getCabinLabel(cabin);
    const isExecutive = label === "Executiva" || label === "Primeira Classe";
    const color = isExecutive ? '#92400E' : '#475569';
    return (
      <span
        style={{
          fontSize: '10px',
          fontWeight: 600,
          color: color,
          border: `1px solid ${color}`,
          borderRadius: '6px',
          padding: '2px 8px',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    );
  };

  const renderSegmentHeader = (segment, label) => {
    const flights = segment?.flights || [];
    const firstFlight = flights[0] || {};
    const lastFlight = flights[flights.length - 1] || firstFlight;

    const originAirport =
      firstFlight.departureAirport || segment?.departureAirport || {};
    const destinationAirport =
      lastFlight.arrivalAirport || segment?.destinationAirport || {};

    const originCode = originAirport.code || segment?.departureCode || "";
    const destinationCode =
      destinationAirport.code || segment?.arrivalCode || "";
    const originCity = originAirport.cityName || "";
    const destinationCity = destinationAirport.cityName || "";

    const companyRaw = segment?.company || firstFlight.carrierCode || "";
    const dateText = formatDate(
      firstFlight.departureDate || segment?.departureDate,
    );
    const stopsText = getStopsLabel(segment?.stops);
    const routeText =
      `(${originCode}) ${originCity} → (${destinationCode}) ${destinationCity}`.trim();

    const hasMultipleFlights = flights.length > 1;

    return (
      <div className="mb-4">
        <div className="d-flex">
          {!hasMultipleFlights && (
            <div
              className="d-flex align-items-center"
              style={{ minWidth: 64 }}
            >
              <img
                src={`${amazonUrl}/${companyRaw}.png`}
                alt={companyRaw}
                style={{ width: 32, objectFit: "contain" }}
              />
            </div>
          )}
          <div className="flex-grow-1 overflow-hidden">
            <p className="font-wl-500 text-sm color-text-dark mb-1">
              {label} • {dateText}
            </p>
            <p className="text-sm text-muted text-truncate mb-0">
              {routeText}
            </p>
            <p className="text-sm text-muted text-truncate mb-0">
              {stopsText} • {passengersCount} passageiro
              {passengersCount === 1 ? "" : "s"}
              {!hasMultipleFlights && flights.length === 1 && (
                <span className="ms-2">
                  <CabinBadge cabin={flights[0].cabin} />
                </span>
              )}
            </p>
          </div>
        </div>

        {hasMultipleFlights && (
          <div className="mt-3 ms-3">
            {flights.map((flight, fIdx) => {
              const depAirport = flight.departureAirport || {};
              const arrAirport = flight.arrivalAirport || {};
              const depCode = depAirport.code || flight.departureStation || "";
              const arrCode = arrAirport.code || flight.arrivalStation || "";
              const depCity = depAirport.cityName || "";
              const arrCity = arrAirport.cityName || "";
              const flightRoute =
                `(${depCode}) ${depCity} → (${arrCode}) ${arrCity}`.trim();
              const cabinText = getCabinLabel(flight.cabin);
              const carrierCode = flight.carrierCode || "";

              return (
                <div key={fIdx} className="d-flex mb-4 gap-3">
                  <div
                    className="d-flex align-items-center"
                    style={{ minWidth: 32 }}
                  >
                    <img
                      src={`${amazonUrl}/${carrierCode}.png`}
                      alt={carrierCode}
                      style={{ width: 32, objectFit: "contain" }}
                    />
                  </div>
                  <div className="flex-grow-1 overflow-hidden">
                    <p className="font-wl-500 text-sm color-text-dark text-truncate mb-0">
                      {flightRoute}
                    </p>
                    <p className="text-sm text-muted mb-0">
                      {passengersCount} passageiro
                      {passengersCount === 1 ? "" : "s"}
                      <span className="ms-2">
                        <CabinBadge cabin={flight.cabin} />
                      </span>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    );
  };

  return (
    <div id="aereoCard">
      {departureLegs.map((segment, idx) => {
        const label = idx === 0 ? "Ida" : `Ida ${idx + 1}`;
        return (
          <Row key={`ida-${idx}`} className="mb-4">
            <Col>{renderSegmentHeader(segment, label)}</Col>
          </Row>
        );
      })}

      {returnLegs.map((segment, idx) => {
        const label = idx === 0 ? "Volta" : `Volta ${idx + 1}`;
        return (
          <Row key={`volta-${idx}`} className="mb-4">
            <Col>{renderSegmentHeader(segment, label)}</Col>
          </Row>
        );
      })}
    </div>
  );
};

AereoSummary.propTypes = {
  envisionData: PropTypes.shape({
    choosenFlight: PropTypes.shape({
      segments: PropTypes.shape({
        sourceCode: PropTypes.string,
        validatingCarrier: PropTypes.string,
        fares: PropTypes.arrayOf(PropTypes.object),
        departureFlights: PropTypes.object,
        returnFlights: PropTypes.object,
        airports: PropTypes.object,
      }),
      passengers: PropTypes.arrayOf(PropTypes.object),
    }),
    segments: PropTypes.shape({
      sourceCode: PropTypes.string,
      validatingCarrier: PropTypes.string,
      fares: PropTypes.arrayOf(PropTypes.object),
      departureFlights: PropTypes.object,
      returnFlights: PropTypes.object,
      airports: PropTypes.object,
    }),
    passengers: PropTypes.arrayOf(PropTypes.object),
    travellers: PropTypes.arrayOf(PropTypes.object),
  }),
};

export default AereoSummary;
